import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { createSkedLinkPublicKey, setTwoFactorKey } from "api/accounts";
import { getUserFriendlyErrorMessage } from "api/UserFriendlyError";

import { updateAccount } from "@/actions/commonActions";
import { ReduxRootState } from "@/types/redux-store";

import { saveAccountSettings, selectFormDisabled } from "./account.model";

type SaveAccountSettingsDispatchResult = {
  error?: unknown;
  payload?: unknown;
};

export type InstagramAccountManagingState = {
  twoFactorKey: string;
  pending: {
    videoThumbnailUpdating: boolean;
    usersUpdating: boolean;
    skedLinkCreating: boolean;
    twoFactorUpdating: boolean;
  };
};

const initialState: InstagramAccountManagingState = {
  twoFactorKey: "",
  pending: {
    videoThumbnailUpdating: false,
    usersUpdating: false,
    skedLinkCreating: false,
    twoFactorUpdating: false,
  },
};

const featureKey = "instagramAccountManaging" as const;

function getFeatureState(state: ReduxRootState): InstagramAccountManagingState {
  return (
    (
      state as ReduxRootState & {
        [featureKey]?: InstagramAccountManagingState;
      }
    )[featureKey] ?? initialState
  );
}

export const saveInstagramVideoThumbnail = createAsyncThunk(
  "instagramAccountManaging/saveVideoThumbnail",
  async (
    {
      accountId,
      changeDefaultThumb,
    }: {
      accountId: string;
      changeDefaultThumb: boolean;
    },
    thunkApi
  ) => {
    const result = (await thunkApi.dispatch(
      saveAccountSettings({
        accountId,
        changes: { changeDefaultThumb },
      }) as never
    )) as SaveAccountSettingsDispatchResult;

    if (saveAccountSettings.rejected.match(result as never)) {
      throw result.error;
    }

    return result.payload;
  }
);

export const generateSkedLinkKey = createAsyncThunk(
  "instagramAccountManaging/generateSkedLinkKey",
  async (accountId: string, thunkApi) => {
    try {
      const accountPublicKey = await createSkedLinkPublicKey(accountId);

      thunkApi.dispatch(updateAccount(accountId, { accountPublicKey }));
      toast.success("Generated public key");

      return { accountId, accountPublicKey };
    } catch (error) {
      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

export const saveInstagramTwoFactorKey = createAsyncThunk(
  "instagramAccountManaging/saveTwoFactorKey",
  async (
    {
      accountId,
      twoFactorKey,
    }: {
      accountId: string;
      twoFactorKey: string;
    },
    thunkApi
  ) => {
    try {
      await setTwoFactorKey({ accountId, twoFactorKey });

      thunkApi.dispatch(updateAccount(accountId, { twoFactorEnabled: true }));
      toast.success("Two factor key updated successfully");

      return { accountId };
    } catch (error) {
      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

const instagramAccountManagingSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {
    twoFactorKeyChanged(state, action: { payload: string }) {
      state.twoFactorKey = action.payload;
    },
    resetTwoFactorKey(state) {
      state.twoFactorKey = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveInstagramVideoThumbnail.pending, (state) => {
      state.pending.videoThumbnailUpdating = true;
    });
    builder.addCase(saveInstagramVideoThumbnail.fulfilled, (state) => {
      state.pending.videoThumbnailUpdating = false;
    });
    builder.addCase(saveInstagramVideoThumbnail.rejected, (state) => {
      state.pending.videoThumbnailUpdating = false;
    });

    builder.addCase(generateSkedLinkKey.pending, (state) => {
      state.pending.skedLinkCreating = true;
    });
    builder.addCase(generateSkedLinkKey.fulfilled, (state) => {
      state.pending.skedLinkCreating = false;
    });
    builder.addCase(generateSkedLinkKey.rejected, (state) => {
      state.pending.skedLinkCreating = false;
    });

    builder.addCase(saveInstagramTwoFactorKey.pending, (state) => {
      state.pending.twoFactorUpdating = true;
    });
    builder.addCase(saveInstagramTwoFactorKey.fulfilled, (state) => {
      state.pending.twoFactorUpdating = false;
      state.twoFactorKey = "";
    });
    builder.addCase(saveInstagramTwoFactorKey.rejected, (state) => {
      state.pending.twoFactorUpdating = false;
    });
  },
});

export const instagramAccountManagingReducer =
  instagramAccountManagingSlice.reducer;
export const instagramAccountManagingActions =
  instagramAccountManagingSlice.actions;

export const selectInstagramUpdatingStatuses = createSelector(
  getFeatureState,
  (instagramState) => instagramState.pending
);

export const selectTwoFactorKeyModel = createSelector(
  getFeatureState,
  (instagramState) => ({
    twoFactorKey: instagramState.twoFactorKey,
    updating: instagramState.pending.twoFactorUpdating,
  })
);

export const selectInstagramFormDisable = createSelector(
  selectFormDisabled,
  selectInstagramUpdatingStatuses,
  (accountFormDisabled, instagramState) =>
    accountFormDisabled ||
    instagramState.videoThumbnailUpdating ||
    instagramState.usersUpdating ||
    instagramState.twoFactorUpdating
);
